// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
  $WithRegattas,
} from 'common.data';

// Global Enums
import {
  AdminStatus,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  adminStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import AdminFields from '../../fields/AdminFields';
import ThQty from '../../common/ThQty';
import ThSort from '../../common/ThSort';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$WithRegattas<$Model<'Admin'>>>;
  dataSortConfig: null | string;
  loading: boolean;
};

const pageId = adminPages.adminAdminsPage;

class AdminAdminsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataSortConfig: null,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Admin') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminAdmin();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async sortData(newDataSortConfig: string): Promise<void> {
    const {
      data,
    } = this.state;

    const [key, direction] = newDataSortConfig.split(':');

    await this.setStateAsync({
      loading: true,
    });

    const sortedData = _.orderBy(
      data,
      [key],
      [direction === 'desc' ? 'desc' : 'asc'],
    );

    await this.setStateAsync({
      data: sortedData,
      dataSortConfig: newDataSortConfig,
      loading: false,
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    const newItem: $ModelUpsertParams<'Admin'> = {
      Email: '',
      InvoiceAddress: null,
      InvoiceName: null,
      InvoiceNo: null,
      InvoiceVat: null,
      Name: '',
      Password: null,
      Phone: null,
      RemoteImage: null,
      Review: false,
      Status: AdminStatus.unconfirmed,
      Super: false,
    };

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <FormDialog
              fields={(
                <AdminFields item={newItem} />
              )}
              id={'create-admin-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'Admin'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertAdminAdmin(params);
                },
              })}
              subTitle={t('New')}
              title={t('Admin')}
            >
              <Button
                color={'info'}
                id={'create-admin-button'}
                size={'sm'}
                title={t('Add new admin')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
            )}
          title={t('Admins')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $WithRegattas<$Model<'Admin'>>) {
    const {
      redirect,
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-admin-social-users-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/admin/${id}/social_users`,
          })}
          size={'sm'}
        >
          {t('Social users')}
        </Button>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-admin-access-tokens-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/admin/${id}/access_tokens`,
          })}
          size={'sm'}
        >
          {t('Access tokens')}
        </Button>
        <FormDialog
          fields={(
            <AdminFields item={dataItem} />
          )}
          id={`update-admin-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Admin'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminAdmin(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Admin')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-admin-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-admin-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminAdmin(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Admin')}
        >
          <Button
            color={'danger'}
            id={`delete-admin-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
      dataSortConfig,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Name')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Name'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Email')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Email'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Phone')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Phone'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Status')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Status'}
            />
            <th>{t('Regattas')}</th>
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Date created')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'createdAt'}
            />
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $WithRegattas<$Model<'Admin'>>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td>{dataItem.Phone}</td>
              <td>{t(adminStatusLabel[dataItem.Status])}</td>
              <td>
                {_.filter(
                  dataItem.Regattas,
                  {
                    Public: true,
                  },
                ).length}
                {' / '}
                {dataItem.Regattas.length}
              </td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminAdminsPageView;
